import { useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";

import { AppShell } from "./components/AppShell.jsx";
import { Spinner } from "./components/ui.jsx";
import { api } from "./lib/api.js";
import { LiveProvider } from "./lib/events.js";
import { Audit } from "./pages/Audit.jsx";
import { Connect } from "./pages/Connect.jsx";
import { Deployments } from "./pages/Deployments.jsx";
import { Domains } from "./pages/Domains.jsx";
import { Live } from "./pages/live/Live.jsx";
import { Overview } from "./pages/Overview.jsx";
import { Ports } from "./pages/Ports.jsx";
import { Projects } from "./pages/Projects.jsx";
import { ProjectDetail } from "./pages/ProjectDetail.jsx";
import { Processes } from "./pages/Processes.jsx";
import { SignIn } from "./pages/SignIn.jsx";
import { Settings } from "./pages/Settings.jsx";
import { Status } from "./pages/Status.jsx";
import { Workspaces } from "./pages/Workspaces.jsx";

/**
 * Everything behind a passkey. The sign-in screen doubles as the first-run
 * enrolment screen, because on a fresh install those are the same moment.
 */
export default function App() {
    const [auth, setAuth] = useState(null);
    const location = useLocation();

    const refresh = useCallback(async () => {
        try {
            const state = await api.auth.state();
            /*
             * A trusted browser resumes under the spinner that is already showing,
             * rather than flashing a sign-in button at somebody who is not going to
             * be asked for anything. Signing out un-trusts the browser, so this
             * never undoes it.
             */
            if (!state.signedIn && state.canResume) {
                try {
                    await api.auth.refresh();
                    setAuth(await api.auth.state());
                    return;
                } catch {
                    // The trust was revoked, or the cookie is stale. Sign in.
                }
            }
            setAuth(state);
        } catch {
            setAuth({ signedIn: false, needsBootstrap: true, unreachable: true });
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    /*
     * The board stands outside the shell and outside the sign-in: a paired
     * screen holds a key rather than a session, and a wall has no sidebar. It
     * does its own asking, and says so on screen when it is not allowed to.
     */
    if (location.pathname === "/live" || location.pathname.startsWith("/live/")) {
        return (
            <Routes>
                <Route path="/live/:layout?" element={<Live />} />
            </Routes>
        );
    }

    if (!auth) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Spinner size={22} className="muted" />
            </div>
        );
    }

    if (!auth.signedIn) return <SignIn auth={auth} onSignedIn={refresh} />;

    // An app asking to connect: behind the sign-in, outside the shell, and
    // straight back to the app once answered.
    if (location.pathname.startsWith("/connect/")) {
        return (
            <Routes>
                <Route path="/connect/:id" element={<Connect />} />
            </Routes>
        );
    }

    return (
        <LiveProvider>
            <AppShell actor={auth.actor} onSignedOut={refresh}>
                <Routes>
                    <Route path="/" element={<Overview />} />
                    <Route path="/projects" element={<Projects />} />
                    <Route path="/projects/:slug" element={<ProjectDetail />} />
                    <Route path="/deployments" element={<Deployments />} />
                    <Route path="/workspaces" element={<Workspaces />} />
                    <Route path="/status" element={<Status />} />
                    <Route path="/processes" element={<Processes />} />
                    <Route path="/ports" element={<Ports />} />
                    <Route path="/domains" element={<Domains />} />
                    <Route path="/audit" element={<Audit />} />
                    <Route path="/settings" element={<Settings />} />
                    {/* Where an approval push lands. */}
                    <Route path="/apps" element={<Navigate to="/settings?tab=apps" replace />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </AppShell>
        </LiveProvider>
    );
}
